import { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  RadioButton as Radio,
  Text,
  TextField
} from '@cencosud-cencommerce/paris-uikit';

import { isDniRegisteredFeatureEnabled } from '@/config/featureFlags';
import {
  type IdentificationDocumentType,
  IdentificationDocumentTypes,
  isDni,
  isRut
} from '@/modules/shared/types/IdentificationDocumentType';
import { FIELD_DOCUMENT_ID } from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/constants';
import type {
  PickupPersonFormField,
  PickupPersonFormValues
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/interfaces';
import {
  DNI_MAX_LENGTH,
  RUT_MAX_LENGTH
} from '@/Pages/BuyNowPage/PaymentAndShipping/partials/PickupOptions/PickupPerson/pickupPersonValidator';

const LABEL_COMPLETE_NAME = 'Nombre completo (*)';
const LABEL_COMPLETE_NAME_HELPER =
  'Nombre y apellido de la persona que hará el retiro.';
const LABEL_PICKUP_RUT = 'RUT (*)';
const LABEL_PICKUP_RUT_HELPER = 'RUT de la persona que hará el retiro.';
const LABEL_DOCUMENT_TYPE = '¿Cuál es tu número de documento?';
const LABEL_DOCUMENT_TYPE_RUT = 'RUT (Chile)';
const LABEL_DOCUMENT_TYPE_DNI = 'DNI (otros países)';
const LABEL_DOCUMENT_NUMBER_RUT = 'RUT (*)';
const LABEL_DOCUMENT_NUMBER_FOREIGN = 'Número de documento (*)';

const COMPLETE_NAME_MAX_LENGTH = 65;

const NON_ALPHANUMERIC_REGEX = /[^a-zA-Z0-9]/g;
const ALPHANUMERIC_CHAR_REGEX = /^[a-zA-Z0-9]$/;

export const PickupPersonFields = () => {
  const [focusedField, setFocusedField] =
    useState<PickupPersonFormField | null>(null);

  const pickupPersonForm = useFormContext<PickupPersonFormValues>();
  const { control, trigger, resetField } = pickupPersonForm;

  const documentType = useWatch<PickupPersonFormValues, 'documentType'>({
    control,
    name: 'documentType',
    defaultValue: IdentificationDocumentTypes.RUT
  });
  const dniEnabled = isDniRegisteredFeatureEnabled();
  const effectiveDocumentType: IdentificationDocumentType = dniEnabled
    ? (documentType ?? IdentificationDocumentTypes.RUT)
    : IdentificationDocumentTypes.RUT;

  const identificationField: keyof PickupPersonFormValues = dniEnabled
    ? 'documentID'
    : 'pickupPersonRut';

  const currentMaxLength =
    dniEnabled && isDni(effectiveDocumentType)
      ? DNI_MAX_LENGTH
      : RUT_MAX_LENGTH;

  return (
    <fieldset className="co-flex co-flex-col co-gap-4">
      <Controller
        name="completeName"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label={LABEL_COMPLETE_NAME}
            maxLength={COMPLETE_NAME_MAX_LENGTH}
            helperText={
              fieldState.error?.message ||
              (focusedField === field.name
                ? LABEL_COMPLETE_NAME_HELPER
                : undefined)
            }
            status={
              fieldState.error
                ? 'error'
                : fieldState.isTouched &&
                    focusedField !== field.name &&
                    field.value
                  ? 'validated'
                  : 'neutral'
            }
            onFocus={() => setFocusedField(field.name)}
            onBlur={() => {
              setFocusedField(null);
              field.onBlur();
            }}
          />
        )}
      />

      {dniEnabled && (
        <div className="co-gap-6 co-flex co-flex-col co-my-2">
          <Text weight={600}>{LABEL_DOCUMENT_TYPE}</Text>
          <Controller
            name="documentType"
            control={control}
            defaultValue={IdentificationDocumentTypes.RUT}
            render={({ field: { value, onChange, ...rest } }) => (
              <div className="co-flex co-flex-col co-gap-3">
                <Radio
                  {...rest}
                  label={LABEL_DOCUMENT_TYPE_RUT}
                  checked={isRut(effectiveDocumentType)}
                  onChange={async () => {
                    onChange(IdentificationDocumentTypes.RUT);
                    resetField?.(FIELD_DOCUMENT_ID, { defaultValue: '' });
                  }}
                  variant="card"
                  className={
                    isRut(effectiveDocumentType)
                      ? '!co-font-medium'
                      : '!co-font-normal'
                  }
                />
                <Radio
                  {...rest}
                  label={LABEL_DOCUMENT_TYPE_DNI}
                  checked={isDni(value as IdentificationDocumentType)}
                  onChange={async () => {
                    onChange(IdentificationDocumentTypes.DNI);
                    resetField?.(FIELD_DOCUMENT_ID, { defaultValue: '' });
                  }}
                  variant="card"
                  className={
                    isDni(value as IdentificationDocumentType)
                      ? '!co-font-medium'
                      : '!co-font-normal'
                  }
                />
              </div>
            )}
          />
        </div>
      )}

      <Controller
        name={identificationField}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            onChange={(e) => {
              let value = (e?.target?.value ?? '') as string;
              if (dniEnabled && isDni(effectiveDocumentType)) {
                value = value.replace(NON_ALPHANUMERIC_REGEX, '');
              }
              field.onChange(value);
              if (fieldState.isTouched) {
                trigger?.(identificationField);
              }
            }}
            onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
              if (dniEnabled && isDni(effectiveDocumentType)) {
                const input = (e.nativeEvent as InputEvent).data || '';
                if (!ALPHANUMERIC_CHAR_REGEX.test(input)) {
                  e.preventDefault();
                }
              }
            }}
            onPaste={async (e: React.ClipboardEvent<HTMLInputElement>) => {
              if (dniEnabled && isDni(effectiveDocumentType)) {
                e.preventDefault();
                const paste = e.clipboardData.getData('text') || '';
                const sanitized = paste.replace(NON_ALPHANUMERIC_REGEX, '');
                field.onChange(sanitized);
                if (fieldState.isTouched) {
                  await trigger?.(identificationField);
                }
              }
            }}
            label={
              dniEnabled
                ? isRut(effectiveDocumentType)
                  ? LABEL_DOCUMENT_NUMBER_RUT
                  : LABEL_DOCUMENT_NUMBER_FOREIGN
                : LABEL_PICKUP_RUT
            }
            type={
              dniEnabled
                ? isRut(effectiveDocumentType)
                  ? 'rut'
                  : 'text'
                : 'rut'
            }
            maxLength={currentMaxLength}
            helperText={
              fieldState.error?.message ||
              (!dniEnabled && focusedField === field.name
                ? LABEL_PICKUP_RUT_HELPER
                : undefined)
            }
            status={
              fieldState.error
                ? 'error'
                : fieldState.isTouched &&
                    focusedField !== field.name &&
                    field.value
                  ? 'validated'
                  : 'neutral'
            }
            onFocus={() => setFocusedField(field.name)}
            onBlur={async () => {
              setFocusedField(null);
              field.onBlur();
              await trigger?.(identificationField);
            }}
          />
        )}
      />
    </fieldset>
  );
};

export default PickupPersonFields;
